import React, { useState } from 'react';
import { Card, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import { FaFilter, FaRedo, FaLayerGroup, FaSortAmountDown } from 'react-icons/fa';

/**
 * UnifiedRestockFilter
 * A consolidated ERP-grade filter component handling:
 * 1. Stock Thresholds (Critical, Low, Reorder)
 * 2. Category Segmentation (Tabs)
 * 3. Custom Logic (< = >)
 */
const UnifiedRestockFilter = ({
    items,
    onFilterChange,
    categories,
    activeCategory,
    onCategoryChange,
    onResetHidden,
    hiddenCount
}) => {
    const [customOperator, setCustomOperator] = useState('<');
    const [customValue, setCustomValue] = useState('');
    const [activeFilter, setActiveFilter] = useState('reorder'); // Default

    const quickFilters = [
        { id: 'zero', label: 'Zero Stock', operator: '=', value: 0, color: 'danger' },
        { id: 'critical', label: 'Critical (< 5)', operator: '<', value: 5, color: 'warning' },
        { id: 'low', label: 'Low (< 10)', operator: '<', value: 10, color: 'info' },
        { id: 'reorder', label: 'Below Reorder', operator: 'reorder', value: null, color: 'primary' }
    ];

    const calculateCount = (f) => {
        if (!items) return 0;
        return items.filter(item => {
            const stock = item.stockTracking?.availableForSale || 0;
            if (f.operator === 'reorder') {
                const reorder = item.stockTracking?.reorderPoint || 0;
                return stock < reorder;
            }
            if (f.operator === '=') return stock === f.value;
            if (f.operator === '<') return stock < f.value;
            return true;
        }).length;
    };

    const handleQuickFilter = (filter) => {
        setActiveFilter(filter.id);
        onFilterChange(filter);
    };

    const handleCustomFilter = (e) => {
        e.preventDefault();
        const val = parseInt(customValue);
        if (isNaN(val)) return;

        setActiveFilter('custom');
        onFilterChange({
            id: 'custom',
            operator: customOperator,
            value: val,
            label: `Stock ${customOperator} ${val}`
        });
    };

    const clearFilter = () => {
        setActiveFilter('reorder');
        setCustomValue('');
        onFilterChange(quickFilters.find(f => f.id === 'reorder'));
    };

    return (
        <div className="unified-filter mb-4">
            <Card className="border-0 shadow-sm overflow-hidden">
                <Card.Header className="bg-white p-3 border-bottom-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        {/* LEFT: STOCK THRESHOLDS */}
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="d-flex align-items-center gap-2 me-2">
                                <FaSortAmountDown className="text-muted" />
                                <span className="fw-bold text-muted small text-uppercase">Stock Level:</span>
                            </div>
                            {quickFilters.map(f => (
                                <Button
                                    key={f.id}
                                    variant={activeFilter === f.id ? f.color : 'outline-light'}
                                    size="sm"
                                    className={`rounded-pill px-3 py-1 d-flex align-items-center gap-2 border ${activeFilter === f.id ? 'shadow-sm fw-bold' : 'text-dark border-secondary bg-white'}`}
                                    onClick={() => handleQuickFilter(f)}
                                >
                                    {f.label}
                                    <Badge bg={activeFilter === f.id ? 'white' : 'secondary'} text={activeFilter === f.id ? f.color : 'white'} className="rounded-circle">
                                        {calculateCount(f)}
                                    </Badge>
                                </Button>
                            ))}
                        </div>

                        {/* RIGHT: CUSTOM LOGIC */}
                        <div className="d-flex align-items-center gap-2">
                            <Form onSubmit={handleCustomFilter} className="d-flex align-items-center gap-2">
                                <span className="fw-bold text-muted small text-uppercase me-1 d-none d-md-inline">Custom:</span>
                                <Form.Select
                                    size="sm"
                                    style={{ width: '70px', fontSize: '0.8rem' }}
                                    value={customOperator}
                                    onChange={(e) => setCustomOperator(e.target.value)}
                                    className="bg-light border-0 fw-bold"
                                >
                                    <option value="=">=</option>
                                    <option value="<">&lt;</option>
                                    <option value="<=">≤</option>
                                    <option value=">">&gt;</option>
                                    <option value=">=">&ge;</option>
                                </Form.Select>
                                <Form.Control
                                    type="number"
                                    size="sm"
                                    style={{ width: '70px', fontSize: '0.8rem' }}
                                    placeholder="Val"
                                    value={customValue}
                                    onChange={(e) => setCustomValue(e.target.value)}
                                    className="bg-light border-0 fw-bold"
                                />
                                <Button
                                    variant="primary"
                                    size="sm"
                                    type="submit"
                                    disabled={customValue === ''}
                                    className="px-3"
                                >
                                    Go
                                </Button>
                                {activeFilter !== 'reorder' && (
                                    <Button variant="link" size="sm" className="text-danger p-0 ms-1" onClick={clearFilter} title="Reset Filters">
                                        <FaRedo />
                                    </Button>
                                )}
                            </Form>
                        </div>
                    </div>
                </Card.Header>

                {/* BOTTOM: CATEGORY TABS (SEGMENTED CONTROL STYLE) */}
                <Card.Body className="bg-light bg-opacity-50 p-2 border-top">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center overflow-auto theme-scroll">
                            <span className="text-muted small fw-bold me-2 ms-2 text-nowrap"><FaLayerGroup className="me-1" /> Category:</span>
                            <div className="custom-brand-tabs m-0 p-1 bg-white border shadow-sm">
                                {categories.map(cat => (
                                    <div className="nav-item" key={cat}>
                                        <button
                                            className={`nav-link border-0 py-1 px-3 ${activeCategory === cat ? 'active' : ''}`}
                                            onClick={() => onCategoryChange(cat)}
                                            style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                                        >
                                            {cat}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {hiddenCount > 0 && (
                            <Button
                                variant="warning"
                                size="sm"
                                className="fw-bold text-dark d-flex align-items-center gap-2 px-3 shadow-sm"
                                onClick={onResetHidden}
                                style={{ fontSize: '0.8rem' }}
                            >
                                <FaRedo /> Restore Hidden ({hiddenCount})
                            </Button>
                        )}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default UnifiedRestockFilter;
